import { useEffect, useRef, useState } from 'react'
import { acharFicha, mudar, semDisco, usarEstado } from './dados/armazem'
import { usarPagamentos } from './dados/pagamentos'
import { conferirSessao, usarSessao, type Eu } from './dados/sessao'
import { ferramentaLigada } from './dominio/ferramentas'
import { CATEGORIAS_NOVAS_R1, calcular, custoPendente } from './dominio/contas'
import { dataBR } from './dominio/numeros'
import { SITUACOES, type Ficha } from './dominio/tipos'
import { Ajustes } from './telas/Ajustes'
import { Custo } from './telas/Custo'
import { CriarSenha, Entrar } from './telas/Entrar'
import { Equipe } from './telas/Equipe'
import { Extrato } from './telas/Extrato'
import { Catira } from './telas/Catira'
import { Notas } from './telas/Notas'
import { Relatorio } from './telas/Relatorio'
import { Plataforma } from './telas/Plataforma'
import { Fichas } from './telas/Fichas'
import { Negociacao } from './telas/Negociacao'
import { Origem } from './telas/Origem'
import { Pagamento } from './telas/Pagamento'
import { Pesar } from './telas/Pesar'
import { Produtores } from './telas/Produtores'
import { Romaneio } from './telas/Romaneio'
import { Avisos, Dialogos, podeSairDaTela, Tarja, type Aba, type Rota } from './ui'
import { pode, somenteConsulta } from './dominio/permissoes'
import { OfertaDeAdocao } from './telas/Adocao'
import { Contas } from './telas/Contas'
import { quando } from './dominio/numeros'
import { periodo } from './dominio/exportar'

const ABAS: [Aba, string][] = [
  ['origem', '1 Origem'], ['negociacao', '2 Negociação'], ['pesagem', '3 Pesagem'], ['romaneio', '4 Romaneio'],
  ['pagamento', 'Pagamento'], ['custo', 'Custo'],
]

/**
 * A porta do app: link de senha (?acesso=…), tela de entrar, ou o app.
 * Sem sinal, quem já entrou neste aparelho segue usando — o curral não tem internet.
 */
export function App() {
  const sessao = usarSessao()
  const [token, setToken] = useState(() => new URLSearchParams(location.search).get('acesso'))
  useEffect(() => { conferirSessao() }, [])
  const tirarToken = () => { history.replaceState(null, '', location.pathname); setToken(null) }

  if (token) return <><CriarSenha token={token} pronto={tirarToken} /><Avisos /><Dialogos /></>
  if (sessao.tipo === 'carregando') return <div className="tela-entrada"><h1>Pesagem</h1><div className="mini-dica">Abrindo…</div></div>
  if (sessao.tipo === 'fora') return <><Entrar /><Avisos /><Dialogos /></>
  return <AppDentro eu={sessao.eu} semSinal={sessao.semSinal} />
}

function AppDentro({ eu, semSinal }: { eu: Eu; semSinal: boolean }) {
  const estado = usarEstado()
  const [rota, setRota] = useState<Rota>({ tela: 'fichas' })
  // R1C: tela com rascunho não gravado (a «Nova pesagem» preenchida) pergunta antes de sumir
  const ir = (r: Rota) => {
    void podeSairDaTela().then((ok) => { if (ok) { setRota(r); window.scrollTo(0, 0) } })
  }
  /**
   * R1C · «Sem sinal agora» ficava na tela depois de sincronizar. O aviso vem
   * da abertura do app (sem sinal ao conferir a sessão); se depois disso uma
   * sincronização deu certo (a hora da última sincronização mudou), havia
   * sinal — o aviso sai.
   */
  const syncNaAbertura = useRef(estado.ultimaSync)
  const mostraSemSinal = semSinal && estado.ultimaSync === syncNaAbertura.current

  // R1C: as categorias novas do gestor (06/10/2026) entram uma vez em cada aparelho
  // (quem tirou alguma depois não a vê voltar)
  useEffect(() => {
    if (estado.ajustes.categoriasR1) return
    mudar((e) => {
      for (const c of CATEGORIAS_NOVAS_R1) if (!e.ajustes.categorias.some((x) => x.toLowerCase() === c.toLowerCase())) e.ajustes.categorias.push(c)
      e.ajustes.categoriasR1 = true
    })
  }, [estado.ajustes.categoriasR1])

  useEffect(() => {
    const t = estado.ajustes.tema
    if (t === 'auto') document.documentElement.removeAttribute('data-tema')
    else document.documentElement.setAttribute('data-tema', t)
  }, [estado.ajustes.tema])

  // os dados da empresa (servidor) são os que saem no romaneio
  useEffect(() => {
    const c = eu.conta
    const em = { nome: c.nome_fantasia, doc: c.doc, telefone: c.telefone, cidade: c.cidade }
    const atual = estado.ajustes.emitente
    if (atual.nome !== em.nome || atual.doc !== em.doc || atual.telefone !== em.telefone || atual.cidade !== em.cidade) {
      mudar((e) => { e.ajustes.emitente = em })
    }
  }, [eu.conta])

  // botão Voltar do celular volta para a lista, em vez de sair do app
  useEffect(() => {
    const voltar = () => {
      void podeSairDaTela().then((ok) => {
        if (ok) setRota({ tela: 'fichas' })
        else history.pushState(null, '') // ficou: o próximo «Voltar» pergunta de novo
      })
    }
    if (rota.tela !== 'fichas') history.pushState(null, '')
    window.addEventListener('popstate', voltar)
    return () => window.removeEventListener('popstate', voltar)
  }, [rota.tela])

  // 3C: o que o gestor ligou em Ajustes › Ferramentas. Desligada, a tela some
  // (e quem estava nela volta às Fichas); os dados continuam sincronizando.
  const comExtrato = ferramentaLigada(eu.conta, 'extrato')
  const comCatira = ferramentaLigada(eu.conta, 'catira')
  const comNotas = ferramentaLigada(eu.conta, 'notas')
  // P3: só gestor e Consulta — o relatório tem as comissões
  const comRelatorio = ferramentaLigada(eu.conta, 'relatorio') && eu.pessoa.papel !== 'comprador'
  // R2B: contas a pagar e a receber — gestor e Consulta, com «Comissões e pagamentos»
  const comContas = ferramentaLigada(eu.conta, 'comissoes') && pode(eu.pessoa.papel, 'verComissao')
  // 3E: a Consulta não abre ficha — some o «Nova» e a rota cai nas Fichas
  const podeCriar = pode(eu.pessoa.papel, 'criarFicha')
  const ficha = rota.tela === 'ficha' ? acharFicha(estado, rota.id) : undefined
  const ativa =rota.tela === 'ficha' || rota.tela === 'nova' || rota.tela === 'catira' || rota.tela === 'notas' || rota.tela === 'relatorio' ? 'fichas' : rota.tela === 'equipe' || rota.tela === 'plataforma' ? 'ajustes' : rota.tela

  return (
    <>
      <header className="topo nao-imprimir">
        <div className="topo-dentro">
          <div>
            <h1>Pesagem</h1>
            <div className="topo-sub">{eu.conta.nome_fantasia} · {eu.pessoa.nome}</div>
          </div>
          {/* 3E: a Consulta vê tudo e não grava nada — o selo avisa antes de ela tentar */}
          {somenteConsulta(eu.pessoa.papel) && <span className="selo-consulta">Somente consulta</span>}
        </div>
      </header>
      <main className="corpo">
        {mostraSemSinal && <div className="nao-imprimir"><Tarja>📵 Sem sinal agora — tudo continua gravando neste aparelho.</Tarja></div>}
        <OfertaDeAdocao eu={eu} />
        {semDisco && <Tarja tipo="erro">Este navegador não está deixando gravar. O que for registrado agora some ao fechar. Abra pelo app instalado ou fora da aba anônima.</Tarja>}
        {(rota.tela === 'fichas' || (rota.tela === 'nova' && !podeCriar)
          || (rota.tela === 'extrato' && !comExtrato) || (rota.tela === 'catira' && !comCatira) || (rota.tela === 'notas' && !comNotas)
          || (rota.tela === 'relatorio' && !comRelatorio))
          && <Fichas ir={ir} />}
        {rota.tela === 'nova' && podeCriar && <><h2 className="secao">Nova pesagem</h2><Origem ir={ir} /></>}
        {rota.tela === 'produtores' && <Produtores />}
        {rota.tela === 'ajustes' && <Ajustes eu={eu} ir={ir} />}
        {rota.tela === 'equipe' && <Equipe souGestor={pode(eu.pessoa.papel, 'equipe')} euId={eu.pessoa.id} />}
        {rota.tela === 'plataforma' && eu.pessoa.plataforma && <Plataforma />}
        {rota.tela === 'extrato' && comExtrato && <Extrato eu={eu} />}
        {/* D3: a entrada fica no topo das Fichas (a barra de baixo já tem 5 botões) */}
        {rota.tela === 'catira' && comCatira && <Catira eu={eu} ir={ir} fichaId={rota.fichaId} />}
        {/* D4: a entrada também fica no topo das Fichas, ao lado da Catira */}
        {rota.tela === 'notas' && comNotas && <Notas eu={eu} ir={ir} />}
        {/* P3: a entrada também fica no topo das Fichas, ao lado da Catira e das Notas */}
        {rota.tela === 'relatorio' && comRelatorio && <Relatorio eu={eu} ir={ir} />}
        {/* R2B: a entrada fica no topo das Fichas, como a do relatório */}
        {rota.tela === 'contas' && comContas && <Contas eu={eu} ir={ir} />}
        {rota.tela === 'contas' && !comContas && <Fichas ir={ir} />}
        {rota.tela === 'ficha' && (ficha
          ? <TelaDaFicha ficha={ficha} aba={rota.aba} ir={ir} eu={eu} />
          : <div className="cartao vazio">Ficha não encontrada.</div>)}
      </main>
      <nav className="navegacao nao-imprimir">
        <button className={ativa === 'fichas' ? 'on' : ''} onClick={() => ir({ tela: 'fichas' })}><span>🗂️</span>Fichas</button>
        {podeCriar && <button onClick={() => ir({ tela: 'nova' })}><span>⚖️</span>Nova</button>}
        <button className={ativa === 'produtores' ? 'on' : ''} onClick={() => ir({ tela: 'produtores' })}><span>👤</span>Produtores</button>
        {comExtrato && <button className={ativa === 'extrato' ? 'on' : ''} onClick={() => ir({ tela: 'extrato' })}><span>🧾</span>Extrato</button>}
        <button className={ativa === 'ajustes' ? 'on' : ''} onClick={() => ir({ tela: 'ajustes' })}><span>⚙️</span>Ajustes</button>
      </nav>
      <Avisos />
      <Dialogos />
    </>
  )
}

function TelaDaFicha({ ficha, aba: pedida, ir, eu }: { ficha: Ficha; aba: Aba; ir: (r: Rota) => void; eu: Eu }) {
  const r = calcular(ficha)
  const gestor = eu.pessoa.papel === 'gestor'
  // 3C: sem «Fechamento para o escritório» a aba Custo some; sem «Comissões e
  // pagamentos», o aviso de comissão some (a comissão continua guardada)
  const comCusto = ferramentaLigada(eu.conta, 'fechamento')
  const comComissoes = ferramentaLigada(eu.conta, 'comissoes')
  const abas = comCusto ? ABAS : ABAS.filter(([a]) => a !== 'custo')
  const aba: Aba = pedida === 'custo' && !comCusto ? 'romaneio' : pedida
  // 2D: aviso só do gestor — o comprador nunca sabe da comissão
  // o hook vem fora da conta: chamado só com a ficha encerrada, o React se perdia
  // ao encerrar ou reabrir («Expected static flag was missing», visto no teste da 3D)
  const pg = usarPagamentos()
  const semComissao = comComissoes && gestor && ficha.situacao === 'encerrada' && !pg.comissoes[ficha.id] && ficha.pesadoPorGestor !== true && ficha.finalidade !== 'Conferência'
  // R1C: «custo pendente» não aparece para o comprador (revisão de 06/10/2026)
  const veCustoPendente = comCusto && eu.pessoa.papel !== 'comprador' && custoPendente(ficha, r)
  return (
    <div>
      <div className="cartao cabeca-ficha nao-imprimir">
        <div className="mini-dica">Nº {ficha.numero} · {dataBR(ficha.data)} · {ficha.finalidade}
          {ficha.versao > 1 ? ` · romaneio v${ficha.versao}` : ''}</div>
        {/* R2B: as duas datas da ficha, cada uma com o nome (nas listas vai uma só, a da Origem) */}
        <div className="mini-dica" style={{ marginTop: 0 }}>aberta em {quando(ficha.criadaEm)}
          {' · '}{ficha.registros.length ? `pesada em ${periodo(ficha)}` : 'ainda sem pesagem'}</div>
        <div className="nome">{ficha.produtor.nome}</div>
        <div className="mini-dica">
          {r.cab} cab{r.kg ? ` · ${r.kg.toLocaleString('pt-BR')} kg` : ''}
          {' · '}<span className={`etiqueta ${ficha.situacao}`}>{SITUACOES[ficha.situacao]}</span>
          {semComissao && <span className="etiqueta aberta clicavel" onClick={() => ir({ tela: 'ficha', id: ficha.id, aba: 'pagamento' })}>comissão pendente</span>}
          {veCustoPendente && <span className="etiqueta aberta clicavel" onClick={() => ir({ tela: 'ficha', id: ficha.id, aba: 'custo' })}>custo pendente</span>}
        </div>
      </div>
      {/* R1C: no celular, duas linhas — passos 1 a 4 em cima; Pagamento e Custo embaixo */}
      <div className="abas nao-imprimir">
        {abas.map(([a, t]) => (
          <button key={a} className={`${a === aba ? 'on' : ''}${/^\d/.test(t) ? '' : ' aba-extra'}`} onClick={() => ir({ tela: 'ficha', id: ficha.id, aba: a })}>{t}</button>
        ))}
      </div>
      {/* key: trocar de ficha recomeça os rascunhos da tela */}
      {aba === 'origem' && <Origem key={ficha.id + ficha.situacao} ficha={ficha} ir={ir} />}
      {aba === 'negociacao' && <Negociacao key={ficha.id + ficha.versao + ficha.situacao} ficha={ficha} ir={ir} />}
      {aba === 'pesagem' && <Pesar key={ficha.id} ficha={ficha} ir={ir} />}
      {aba === 'romaneio' && <Romaneio ficha={ficha} ir={ir} />}
      {aba === 'pagamento' && <Pagamento ficha={ficha} />}
      {aba === 'custo' && <Custo ficha={ficha} ir={ir} />}
    </div>
  )
}
